import { useEffect, useRef } from 'react';
import logError from '../../utils/logError';
import { getUserMetadata, UserMetadata } from './adaRequests';
import useAdaToken from './useAdaToken';

export type Args = {
  fields?: UserMetadata;
  config: {
    handle: string | null;
    language: string | null;
  };
  experiment?: boolean;
};

const defaultHandle = 'alltrails';
const defaultLanguage = 'en';

function delay(milliseconds: number) {
  return new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });
}

async function refreshUserMetadata(adaEmbed: Window['adaEmbed'], attemptNumber = 1) {
  try {
    const userMetadata = await getUserMetadata();
    window.adaEmbed.setMetaFields(userMetadata);
  } catch (error) {
    if (attemptNumber < 3) {
      logError(new Error('Ada user metadata refresh failed', { cause: error }), event => {
        event.addMetadata('ada', {
          retryCount: attemptNumber,
          action: 'refresh_user_metadata_failed'
        });
      });

      await delay(500);
      refreshUserMetadata(adaEmbed, attemptNumber + 1);
    } else {
      logError(new Error('Ada user metadata refresh retries reached', { cause: error }), event => {
        event.addMetadata('ada', {
          retryCount: attemptNumber,
          action: 'refresh_user_metadata_retries_exceeded'
        });
      });
    }
  }
}

function updateScriptExperimentClass(scriptElement: Element, experimentClass: string, isExperiment: boolean) {
  if (isExperiment) {
    scriptElement.classList.add(experimentClass);
  } else {
    scriptElement.classList.remove(experimentClass);
  }
}

export default function AdaChatbot({ fields, config, experiment = false }: Args) {
  const adaHasBeenOpenedBefore = useRef(false);
  const { resetAdaTokenHandler } = useAdaToken();

  useEffect(() => {
    const { handle, language } = config;
    window.adaSettings = {
      eventCallbacks: {
        // In the Ada configuration we fire this event after performing actions
        // that may have changed the metadata.
        refresh_user_data: () => {
          refreshUserMetadata(window.adaEmbed);
        }
      },
      toggleCallback: async (isDrawerOpen: boolean) => {
        if (isDrawerOpen) {
          // Upon re-opening Ada, we'll refresh the user metadata using the latest
          // data from the API.
          if (adaHasBeenOpenedBefore.current || !fields) {
            refreshUserMetadata(window.adaEmbed);
          } else {
            window.adaEmbed.setMetaFields(fields);
          }

          adaHasBeenOpenedBefore.current = true;
        }
      },
      // This is called when the Ada chatbot is ready but not necessarily opened by a chatter.
      adaReadyCallback: ({ isRolledOut }: { isRolledOut: boolean }) => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const isAda = urlParams.get('ref') === 'ada';

        if (isRolledOut) {
          // This will initially set the alltrailsUserToken in Ada, and then refresh it periodically.
          // Doing it here instead of in the chatterTokenCallback ensures that the token is set before the chatbot is opened
          // hopefully to avoid any issues with the token not being set in time.
          resetAdaTokenHandler();
          if (isAda) {
            // open chat window
            window.adaEmbed.toggle();
          }
        }
      },
      handle: handle || defaultHandle,
      language: language || defaultLanguage
    };
  }, [fields, config, resetAdaTokenHandler]);

  useEffect(() => {
    const adaScriptSrc = 'https://static.ada.support/embed2.js';
    const experimentClass = 'ada-experiment';
    // Check if script already exists to prevent duplicate script error
    const existingScript = document.querySelector(`script[src="${adaScriptSrc}"]`);
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = adaScriptSrc;
      script.async = true;
      script.id = '__ada';
      script.defer = true;

      if (experiment) {
        script.className = experimentClass;
      }
      document.body.appendChild(script);
    } else {
      // If the script already exists, update the experiment class if needed
      updateScriptExperimentClass(existingScript, experimentClass, experiment);
    }
  }, [experiment]);

  return null;
}
